import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { Download, Mail, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/api";
import { useAdminAuth } from "../../context/AdminAuthContext";

const AdminNewsletter = () => {
  const { authHeaders } = useOutletContext();
  const { token } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingEmail, setRemovingEmail] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/admin/newsletter`, { headers: authHeaders })
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Laden mislukt"))
      .finally(() => setLoading(false));
  }, [authHeaders]);

  const downloadCSV = async () => {
    try {
      const res = await axios.get(`${API}/admin/newsletter/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `newsletter-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export gedownload");
    } catch {
      toast.error("Export mislukt");
    }
  };

  const removeSubscriber = async (email) => {
    if (!window.confirm(`Weet je zeker dat je ${email} wilt verwijderen uit de nieuwsbrieflijst?`)) {
      return;
    }
    setRemovingEmail(email);
    try {
      await axios.delete(`${API}/admin/newsletter/${encodeURIComponent(email)}`, {
        headers: authHeaders,
      });
      setItems((prev) => prev.filter((it) => it.email !== email));
      toast.success(`${email} verwijderd`);
    } catch {
      toast.error("Verwijderen mislukt");
    } finally {
      setRemovingEmail(null);
    }
  };

  return (
    <div data-testid="admin-newsletter-page" className="border border-[var(--tha-border)] bg-[#0a0a0a]">
      <div className="px-5 py-4 border-b border-[var(--tha-border)] flex items-center gap-2">
        <Mail size={14} className="text-[var(--tha-red)]" />
        <span className="text-xs font-mono-x tracking-widest text-white">NIEUWSBRIEFLEDEN</span>
        <span className="ml-auto text-xs text-[var(--tha-muted)]">{items.length} aanmeldingen</span>
        <button
          data-testid="export-csv-button"
          onClick={downloadCSV}
          disabled={items.length === 0}
          className="btn-ghost-red px-3 py-2 inline-flex items-center gap-2 text-xs disabled:opacity-50"
        >
          <Download size={12} /> EXPORT CSV
        </button>
      </div>

      {loading ? (
        <div className="p-12 text-center text-sm text-[var(--tha-muted)]">Laden...</div>
      ) : items.length === 0 ? (
        <div className="p-12 text-center text-sm text-[var(--tha-muted)]">
          Nog geen aanmeldingen.
        </div>
      ) : (
        <ul>
          {items.map((i) => (
            <li
              key={i.id}
              data-testid={`subscriber-row-${i.email}`}
              className="px-5 py-3 border-b border-[var(--tha-border)] last:border-0 flex justify-between items-center gap-4"
            >
              <span className="text-sm text-white truncate">{i.email}</span>
              <div className="flex items-center gap-4 shrink-0">
                <span className="text-xs text-[var(--tha-muted)] font-mono-x hidden sm:inline">
                  {new Date(i.created_at).toLocaleString("nl-NL")}
                </span>
                <button
                  data-testid={`delete-subscriber-${i.email}`}
                  onClick={() => removeSubscriber(i.email)}
                  disabled={removingEmail === i.email}
                  className="btn-ghost-red p-2 inline-flex items-center gap-1 text-xs disabled:opacity-50"
                  aria-label={`Verwijder ${i.email}`}
                  title="Verwijderen"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AdminNewsletter;
